import type { LogsEvent } from "@datadog/browser-logs";
import type { RumEvent } from "@datadog/browser-rum";

// Browser extensions run content scripts inside our page, so their uncaught
// errors reach `window.onerror` and the SDK reports them as ours. One
// extension (`chrome-extension://eppiocemhmnlbhjplcgkofciiegomcon`) throws
// "Cannot read properties of undefined (reading 'M_ID')" every ~31s from a
// single workstation: 12,625 of the 59,882 events on the prod error monitor in
// the 7 days to 2026-10-27 (ENG-2600), none of it ours to fix. Same treatment as
// ENG-2354: Logs gets it as a tagged `warn` (off the error monitor, rate still
// visible) and RUM keeps it, tagged so it can be filtered out.
export const BROWSER_EXTENSION_ERROR_TAG = "browser_extension_error";

const EXTENSION_SCHEMES = [
  "chrome-extension:",
  "moz-extension:",
  "safari-extension:",
  "safari-web-extension:"
];

// SDK stack frames read `at fn @ url:line:col`; frames without a URL
// (`<anonymous>`, native code) say nothing about who threw.
function frameUrls(stack: string): string[] {
  return stack
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.startsWith("at "))
    .map((line) => line.split(" @ ")[1])
    .filter((url): url is string => Boolean(url) && url.includes("://"));
}

const isExtensionUrl = (url: string) =>
  EXTENSION_SCHEMES.some((scheme) => url.startsWith(scheme));

/**
 * True when every frame with a URL is extension code. One app frame anywhere
 * keeps the error ours: an extension that wraps `fetch` sits above our own
 * frames in a real failed request, and that request still counts.
 */
export function isBrowserExtensionStack(stack: string | undefined): boolean {
  const urls = stack ? frameUrls(stack) : [];
  return urls.length > 0 && urls.every(isExtensionUrl);
}

/**
 * Logs `beforeSend`: turns this error into a tagged `warn` in place, and
 * removes its `error` block so nothing keyed on `@error.*` still counts it.
 */
export function downgradeBrowserExtensionLog(log: LogsEvent): void {
  if (!isBrowserExtensionStack(log.error?.stack)) return;
  log.status = "warn";
  log[BROWSER_EXTENSION_ERROR_TAG] = true;
  delete log.error;
}

/** RUM `beforeSend`: tags this error in its context; the event is kept. */
export function tagBrowserExtensionRumError(event: RumEvent): void {
  if (event.type !== "error" || !isBrowserExtensionStack(event.error.stack)) {
    return;
  }
  event.context = { ...event.context, [BROWSER_EXTENSION_ERROR_TAG]: true };
}
